/**
 * jiraSnapshot — lift a Jira project read into a SemanticSnapshot.
 *
 * The same thin adapter shape as linearSnapshot / notionSnapshot, over the SAME
 * shared reference-dialect builder: Jira issues are reference objects, issue
 * links are typed cross-references (the link name is the cue), and the
 * epic/parent chain is the parent tree. That this is nearly identical to the
 * Linear adapter is the point — one reference-dialect builder, now four
 * dialects. Jira asserts no category (status colour is workflow phase, not item
 * semantics), so no colorRules are passed.
 */

import type { JiraProjectRead } from './jiraSemanticApi';
import type { MiroColorRule, RelationshipCueRule, TypeMapRule } from '../fieldMappingRegistry';
import { JIRA_DEFAULT_RELATIONSHIP_MAP, JIRA_UNNAMED_RELATION } from './profiles/jira';
import {
  buildReferenceSnapshot,
  type ReferenceDialectRead,
} from './referenceDialectSnapshot';
import type { SemanticSnapshot } from './snapshot';

export interface JiraSnapshotMaps {
  relationshipMap?: RelationshipCueRule[];
  typeMap?: TypeMapRule[];
  colorRules?: MiroColorRule[];
}

export function buildJiraSnapshot(
  read: JiraProjectRead,
  maps: JiraSnapshotMaps,
  options: { capturedAt?: string } = {},
): SemanticSnapshot {
  const dialectRead: ReferenceDialectRead = {
    connectorType: 'jira',
    container: { id: read.project.id, title: read.project.name, url: read.project.url },
    objects: read.issues.map(issue => ({
      externalId: issue.externalId,
      ...(issue.externalUrl ? { externalUrl: issue.externalUrl } : {}),
      title: issue.title,
      ...(issue.description ? { description: issue.description } : {}),
      ...(issue.parentExternalId ? { parentExternalId: issue.parentExternalId } : {}),
      relations: issue.relations.map(r => ({ cue: r.type, targetExternalIds: r.targetExternalIds })),
    })),
  };

  return buildReferenceSnapshot(
    dialectRead,
    maps,
    {
      relationshipMap: JIRA_DEFAULT_RELATIONSHIP_MAP,
      containmentCueValue: 'parent',
      unnamedRelation: JIRA_UNNAMED_RELATION,
    },
    options,
  );
}
